// 給与：日給の「支給対象日数」の保存（スプリント02／F2・D-13）。
//
// 置き場の決め方（既存の作法に合わせる）:
//   - 本番（Supabase 接続時）: settings テーブルの **payrollDays** ドキュメント。
//     形は { months: { 'YYYY-MM': { '<乗務員コード>': 日数 } }, updatedAt }。
//     既存の settings/payroll（人件費率の旧系統・技術的負債の片割れ）とは**別ドキュメント**にする。
//     spec §2.1 が「settings/payroll の現在の用途を壊すな」と明記しているため、あのキーには読み書きしない。
//   - デモ（Supabase 未設定 / デモ表示）: ブラウザローカル（localStorage）。
//     乗務員マスタのデモ保管庫（driverMasters.js）と同じ作法。端末にだけ残る。
//
// 過去月も同じ構造で持てる（月をキーにするだけ）＝ D-13「取込済みの月すべてに遡れる」を満たす。
import { COLLECTIONS, getOne, upsert } from '../collections.js'
import { supabaseReady } from '../../supabase.js'
import { normDays } from './workdays.core.js'

const LS_KEY = 'veil-neb.payroll.days'
const DOC_ID = 'payrollDays'

const normCode = (c) => String(c == null ? '' : c).trim().replace(/^0+(?=\d)/, '')
const isMonth = (m) => /^\d{4}-\d{2}$/.test(String(m || ''))

// 保存済みの塊を { 'YYYY-MM': { code: 0〜31 } } へ正規化する。
// 壊れた値・範囲外の値をここで落とす＝画面と計算コアに不正値を流さない。
export function normDaysMap(raw) {
  const out = {}
  for (const m in (raw || {})) {
    if (!isMonth(m)) continue
    const src = raw[m]
    if (!src || typeof src !== 'object') continue
    const month = {}
    for (const k in src) {
      const code = normCode(k)
      const d = normDays(src[k])
      if (code && d != null) month[code] = d
    }
    if (Object.keys(month).length) out[m] = month
  }
  return out
}

let _mem = null // 直近に読んだ全月マップ。保存時の土台にする（部分更新で他の月を消さないため）
let _chain = Promise.resolve() // 連続保存の直列化（読み→書きの競合で月が飛ぶのを防ぐ）

function loadLocal() {
  try { return normDaysMap(JSON.parse(localStorage.getItem(LS_KEY) || '{}')) } catch { return {} }
}
function saveLocal(all) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(all)) } catch { /* private mode 等。保存が効かないだけ */ }
}

// 全月ぶんを読む（月を切り替えるたびに取りに行かなくて済むよう、まとめて返す）。
// online＝実データ表示（!ctx.demo && supabaseReady）。デモは端末ローカル。
export async function loadPayrollDays(online) {
  if (!online || !supabaseReady) { _mem = loadLocal(); return _mem }
  try {
    const doc = await getOne(COLLECTIONS.settings, DOC_ID)
    _mem = normDaysMap(doc && doc.months)
  } catch {
    _mem = {} // 取得できないときは「未入力」として扱う（誤った日数で額を出さない）
  }
  return _mem
}

/**
 * 1 名・1 か月ぶんを保存する。days が null なら未入力へ戻す（＝額を空欄に戻す）。
 * 返り値は保存後の全月マップ。
 */
export async function savePayrollDay(online, month, code, days) {
  const m = String(month || '')
  const c = normCode(code)
  const d = normDays(days)
  if (!isMonth(m) || !c) return _mem || {}
  const all = _mem || (_mem = {})
  const cur = all[m] || (all[m] = {})
  if (d == null) delete cur[c]
  else cur[c] = d
  if (!Object.keys(cur).length) delete all[m]

  if (!online || !supabaseReady) { saveLocal(all); return all }
  const snapshot = JSON.parse(JSON.stringify(all))
  _chain = _chain.then(() => upsert(COLLECTIONS.settings, DOC_ID, { months: snapshot, updatedAt: new Date().toISOString() })).catch(() => { /* 呼び側が画面で通知する */ })
  await _chain
  return all
}

// テスト・画面リセット用。
export function clearPayrollDaysCache() { _mem = null }
export function clearLocalPayrollDays() { try { localStorage.removeItem(LS_KEY) } catch { /* noop */ } }
export { LS_KEY as PAYROLL_DAYS_LS_KEY }
